import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TrialCta from "../../../../common/components/TrialCta";
import familyPlanning from "../../../../assets/family.png";
// TODO(refresh): the handoff calls for dedicated carousel photos. These two are
// the FAQ and Blog banner photos, reused until slide images are supplied.
import familyApart from "../../../../assets/new-images/faq-caros.png";
import recoveryInfo from "../../../../assets/new-images/blog-caros.png";

const SLIDE_INTERVAL_MS = 6000;

const slides = [
  {
    image: familyPlanning,
    alt: "Three generations of a family smiling together on a sofa at home.",
    message: "Plan together, so everyone in your household knows what to do.",
    action: { label: "Explore family features", to: "/ourServices/my-mrf-plan" },
  },
  {
    image: familyApart,
    alt: "A woman talks on the phone while reviewing her family's plan on a laptop.",
    message: "When your family is in different places, know how you'll reach one another.",
    action: { label: "See the MYREDFOLDER® App", to: "/ourServices/my-mrf-app" },
  },
  {
    image: recoveryInfo,
    alt: "Two people reviewing household records together on a laptop.",
    message: "Keep records, contacts, and property details organized for recovery.",
    action: { label: "See how it works", href: "#how-it-works" },
  },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Router's ScrollToTop resets scroll on any location change, including hash
// changes, so in-page links scroll themselves instead of changing the URL.
export const scrollToId = (event, id, block = "start") => {
  const target = document.getElementById(id);
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block });
  target.focus({ preventScroll: true });
};

const SlideAction = ({ action }) =>
  action.href ? (
    <a className="mrf-btn mrf-btn--on-dark" href={action.href} onClick={(e) => scrollToId(e, action.href.slice(1))}>
      {action.label}
    </a>
  ) : (
    <Link className="mrf-btn mrf-btn--on-dark" to={action.to}>
      {action.label}
    </Link>
  );

const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(!prefersReducedMotion());
  const [hovered, setHovered] = useState(false);

  const go = useCallback((next) => setIndex((next + slides.length) % slides.length), []);

  useEffect(() => {
    if (!playing || hovered) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % slides.length), SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [playing, hovered]);

  return (
    <section
      className="mrf-carousel"
      aria-roledescription="carousel"
      aria-label="How MYREDFOLDER® helps families"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <div aria-live={playing ? "off" : "polite"}>
        {slides.map((slide, i) => (
          <div
            key={slide.message}
            className={`mrf-carousel__slide${i === index ? " is-active" : ""}`}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}`}
            aria-hidden={i !== index}
          >
            <img className="mrf-carousel__img" src={slide.image} alt={slide.alt} />
            <div className="mrf-carousel__caption">
              <p className="mrf-carousel__message">{slide.message}</p>
              <SlideAction action={slide.action} />
            </div>
          </div>
        ))}
      </div>

      <div className="mrf-carousel__dots">
        {slides.map((slide, i) => (
          <button
            key={slide.message}
            type="button"
            className="mrf-carousel__dot"
            aria-label={`Show slide ${i + 1}`}
            aria-current={i === index}
            onClick={() => go(i)}
          />
        ))}
      </div>

      <div className="mrf-carousel__controls">
        <button type="button" className="mrf-carousel__control" aria-label="Previous slide" onClick={() => go(index - 1)}>
          <ChevronLeftIcon aria-hidden="true" />
        </button>
        <button
          type="button"
          className="mrf-carousel__control"
          aria-label={playing ? "Pause slideshow" : "Play slideshow"}
          onClick={() => setPlaying((p) => !p)}
        >
          {playing ? <PauseIcon aria-hidden="true" /> : <PlayArrowIcon aria-hidden="true" />}
        </button>
        <button type="button" className="mrf-carousel__control" aria-label="Next slide" onClick={() => go(index + 1)}>
          <ChevronRightIcon aria-hidden="true" />
        </button>
      </div>
    </section>
  );
};

const HomeHero = () => (
  <div className="mrf-hero">
    <div className="mrf-hero__grid">
      <div className="mrf-hero__copy">
        <h1 className="mrf-hero__title">When plans change in minutes, your family should know what to do.</h1>
        <p className="mrf-lead">
          Build a family emergency plan, agree on meeting places, stay connected, and keep the information you need in
          one place. MYREDFOLDER® helps your household prepare before, respond during, and organize recovery after an
          emergency.
        </p>
        <div className="mrf-hero__actions">
          <TrialCta />
          <a className="mrf-btn mrf-btn--on-dark" href="#about-video" onClick={(e) => scrollToId(e, "about-video", "center")}>
            See how MYREDFOLDER works
          </a>
        </div>
        <p className="mrf-hero__note">Offered by MYREDFOLDER, Inc. · Administered by NEPTA</p>
      </div>
      <HeroCarousel />
    </div>
  </div>
);

export default HomeHero;
